import { memo, useEffect, useMemo, useRef } from "react";
import { Handle, Position, NodeProps, useUpdateNodeInternals } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { CircleUser, Globe } from "lucide-react";
import { useIntegrations } from "../../hooks/useIntegrations";
import { SyncInjectorNodeData, HandleConfig } from "../../types";
import { getStorageUrl } from "@utils/utils";
import { useNodeResizing } from '../../hooks/useNodeResizing';
import { NODE_DIMENSIONS } from '../../utils/nodeHelpers';

// Helper function to calculate dynamic style for handles on a side
const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));

  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
    case Position.Right:
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

function SyncInjectorNode({
  data,
  selected,
  id
}: NodeProps<SyncInjectorNodeData>) {
  const { integrations, loadIntegrations } = useIntegrations();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeContentRef = useRef<HTMLDivElement>(null);

  // Calculate minimum height based on content
  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;

      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;
      if (showLabels) {
        let visibleLabelCount = 0;
        if (data.customLabel && data.customLabelVisibility !== "hidden") visibleLabelCount++;
        if (data.crossDomain && data.crossDomainVisibility !== "hidden") visibleLabelCount++;

        if (visibleLabelCount > 0) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
        if (visibleLabelCount > 1 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }

      const filteredIntegrations = integrations.filter((integration) =>
        data.idSyncs?.includes(integration.id) &&
        (integration.sync_injector === true),
      );
      if (filteredIntegrations.length > 0 && data.idSyncsVisibility !== "hidden") {
        calculatedHeight += NODE_DIMENSIONS.AVATAR_STACK_MIN_VISIBLE_HEIGHT_ADDITION;
      }
      
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data, integrations]);

  // Use the node resizing hook
  const { 
    onResize, 
    onResizeEnd, 
    currentWidth, 
    currentHeight,
    effectiveMinHeight 
  } = useNodeResizing({
    id,
    nodeType: 'sync-injector', // Pass the node type
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight
  });

  // Effect to update node internals when handles change
  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  // --- Handle Rendering Logic ---
  const handles = useMemo(() => {
    // Default to a single source handle if missing
    const defaultSyncHandles: HandleConfig[] = [
      { id: 'default-source', type: 'source', position: Position.Right, color: '#6366F1' }, // Indigo Source
    ];
    return Array.isArray(data.handles) ? [...data.handles] : defaultSyncHandles;
  }, [data.handles]);

  const handlesByPosition = useMemo(() => {
    return handles.reduce((acc, handle) => {
      if (!acc[handle.position]) {
        acc[handle.position] = [];
      }
      acc[handle.position].push(handle);
      return acc;
    }, {} as Record<Position, HandleConfig[]>);
  }, [handles]);
  // --- End Handle Rendering Logic ---

  const filteredIntegrations = integrations.filter((integration) =>
    data.idSyncs?.includes(integration.id) &&
    (integration.sync_injector === true),
  );

  const LOGO_AVATAR_WIDTH = 20; // h-5 w-5
  const LOGO_AVATAR_OFFSET = 16; // left: ${index * 16}px

  // Calculate available width for logos within the node
  // currentWidth - (pl-3 + pr-4 from parent div) - (mr-2 from logo stack container)
  const effectiveAvailableWidth = currentWidth - 28 - 8;

  let maxVisibleLogos = 0;
  if (effectiveAvailableWidth >= LOGO_AVATAR_WIDTH) {
    maxVisibleLogos = 1 + Math.floor((effectiveAvailableWidth - LOGO_AVATAR_WIDTH) / LOGO_AVATAR_OFFSET);
  }

  let logosToDisplay = filteredIntegrations.length;
  let showMoreIndicator = false;

  if (filteredIntegrations.length > maxVisibleLogos) {
    if (maxVisibleLogos >= 1) { // Need space for at least one logo + indicator
      logosToDisplay = maxVisibleLogos - 1; // Show one less to make space for +X
      showMoreIndicator = true;
    } else {
      logosToDisplay = 0; // No space for any logo or indicator
      showMoreIndicator = false;
    }
  }

  // Ensure logosToDisplay is not negative
  logosToDisplay = Math.max(0, logosToDisplay);

  // Calculate the actual width of the displayed logo stack for centering
  let calculatedLogoStackWidth = 0;
  if (logosToDisplay > 0) {
    calculatedLogoStackWidth = LOGO_AVATAR_WIDTH + (logosToDisplay - 1) * LOGO_AVATAR_OFFSET;
  }
  if (showMoreIndicator) {
    // The +X indicator takes the place of a logo, so its position is at logosToDisplay * LOGO_AVATAR_OFFSET
    // and it adds its own width to the total stack width.
    calculatedLogoStackWidth = LOGO_AVATAR_WIDTH + (logosToDisplay) * LOGO_AVATAR_OFFSET;
  }

  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6', // blue-500
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}
      
      <div
        className={`
          w-full h-full pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
          flex flex-col justify-center items-center
            ${selected 
              ? "border-indigo-500 shadow-indigo-100 outline outline-4 outline-indigo-500/20 outline-offset-0" 
              : "border-gray-300 dark:border-gray-600"
            }
        `}
      >
        <div ref={nodeContentRef} className="flex items-center justify-center gap-2 node-content">
          <div className="w-6 h-6 rounded-full bg-indigo-50 dark:bg-indigo-400/20 text-indigo-600 dark:text-indigo-200 flex items-center justify-center flex-shrink-0">
            <CircleUser className="h-4 w-4" />
          </div>
          <div className="min-w-0 content-container">
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {data.label}
            </h3>
            
            {currentWidth >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD && (
              <>
                {/* Custom Label - only show if visibility is not hidden */}
                {data.customLabel && data.customLabelVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-blue-100 text-blue-800"
                  >
                    {data.customLabel}
                  </span>
                )}

                {/* Only render the Cross-domain label if it's enabled and visible */}
                {data.crossDomain && data.crossDomainVisibility !== "hidden" && (
                  <div className="flex flex-wrap gap-1">
                    <div
                      className="mt-1 flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded text-xs text-gray-600 dark:text-gray-100"
                    >
                      <Globe className="h-3 w-3" />
                      <span>Cross-domain</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Only show vendor avatar stack if there are integrations and visibility is not hidden */}
        {currentHeight >= NODE_DIMENSIONS.HIDE_NOTES_HEIGHT_THRESHOLD && filteredIntegrations.length > 0 && data.idSyncsVisibility !== "hidden" && (
          <div className="relative h-7 mt-2 mx-auto" style={{ 
            width: `${calculatedLogoStackWidth}px`
          }}>
            {filteredIntegrations.slice(0, logosToDisplay).map((integration, index) => (
              <img
                key={integration.id}
                src={getStorageUrl(integration.logo_url || '')}
                alt={integration.name}
                className="absolute h-5 w-5 rounded-full border-white"
                style={{
                  left: `${index * LOGO_AVATAR_OFFSET}px`,
                  zIndex: filteredIntegrations.length + index,
                }}
              />
            ))}
            {showMoreIndicator && (
              <div
                className="absolute h-5 w-5 rounded-full border-white bg-gray-300 text-[10px] font-medium flex items-center justify-center"
                style={{
                  left: `${logosToDisplay * LOGO_AVATAR_OFFSET}px`,
                  zIndex: filteredIntegrations.length + logosToDisplay,
                }}
              >
                +{filteredIntegrations.length - logosToDisplay}
              </div>
            )}
          </div>
        )}

        {/* --- Handle Rendering --- */}
        {Object.entries(handlesByPosition).map(([_, positionHandles]) =>
          positionHandles.map((handle, index) => (
            <Handle
              key={`${handle.id}-${handle.color}`} // Use unique key
              id={handle.id}
              type={handle.type}
              position={handle.position as Position}
              className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`} // Style like DestinationNode
              style={{
                ...calculateHandleStyle(index, positionHandles.length, handle.position),
                backgroundColor: handle.color, // Use configured color directly
              }}
              // Add isValidConnection if needed
            />
          ))
        )}
        {/* --- End Handle Rendering --- */}
      </div>
    </div>
  );
}

export default memo(SyncInjectorNode);
